import * as Sentry from '@sentry/vue';

const SERVICE_WORKER_FINGERPRINT = 'service-worker-update-flow' as const;

// 'activation-timeout' is intentionally excluded: it is a benign, high-volume
// event (the new SW just took longer than the wait window) that inflated the
// Sentry dashboard, so it stays a breadcrumb rather than a captured warning.
const CAPTURED_EVENT_TYPES: ReadonlySet<string> = new Set([
  'register-error',
  'waiting-missing',
  'update-rejected',
  'reload-guard-exhausted',
]);

export interface ServiceWorkerEventPayload {
  type: string;
  message?: string;
  data?: Record<string, unknown>;
}

/**
 * Records a service worker update-flow event as a Sentry breadcrumb and, for
 * the pathological paths, captures a warning. No-ops when Sentry is not
 * initialised.
 */
export function reportServiceWorkerEventToSentry(
  payload: ServiceWorkerEventPayload,
): void {
  const client = Sentry.getClient();
  if (!client) {
    return;
  }

  const isCaptured = CAPTURED_EVENT_TYPES.has(payload.type);

  Sentry.addBreadcrumb({
    category: 'service-worker',
    message: payload.message
      ? `${payload.type}: ${payload.message}`
      : payload.type,
    level: isCaptured ? 'warning' : 'info',
    ...(payload.data ? { data: payload.data } : {}),
  });

  if (!isCaptured) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('service_worker.event', payload.type);
    scope.setContext('service_worker', {
      type: payload.type,
      ...(payload.message !== undefined ? { message: payload.message } : {}),
      ...(payload.data ?? {}),
    });
    scope.setFingerprint([SERVICE_WORKER_FINGERPRINT, payload.type]);
    Sentry.captureMessage(`Service worker update flow: ${payload.type}`);
  });
}
